import { memo, useCallback, useMemo } from 'react'

import { ListItemIcon, Menu, MenuItem } from '@mui/material'
import { DeleteOutlineOutlined, EditOutlined, MoreVertOutlined, VisibilityOutlined } from '@mui/icons-material'

import { IconButton, Tooltip, useDialogState, usePopoverState } from '@collaborate-health/uikit'
import { TrainingMaterialBaseResponse } from '@tabeeb/contracts/microservices/swagger-article-service'

import TrainingMaterialDeleteConfirmationDialog from '../TrainingMaterialDeleteConfirmationDialog'

export interface TrainingMaterialMenuProps {
  trainingMaterial: TrainingMaterialBaseResponse
  onDelete?: (trainingMaterial: TrainingMaterialBaseResponse) => void
  onEdit?: (trainingMaterial: TrainingMaterialBaseResponse) => void
  onView?: (trainingMaterial: TrainingMaterialBaseResponse) => void
}

const TrainingMaterialMenu: React.FC<TrainingMaterialMenuProps> = ({ trainingMaterial, onDelete, onEdit, onView }) => {
  const [menuAnchorRef, isMenuOpen, onOpenMenu, onCloseMenu] = usePopoverState<HTMLButtonElement>()
  const [isDeleteConfirmationDialogOpen, onOpenDeleteConfirmationDialog, onCloseDeleteConfirmationDialog] =
    useDialogState()

  const onDeleteSubmit = useCallback(() => {
    onCloseDeleteConfirmationDialog()
    onDelete?.(trainingMaterial)
  }, [onCloseDeleteConfirmationDialog, onDelete, trainingMaterial])

  const options = useMemo(() => {
    const menuOptions = []

    if (onEdit) {
      menuOptions.push({
        id: 'edit',
        title: 'Edit',
        icon: <EditOutlined />,
        onClick: () => onEdit(trainingMaterial),
      })
    }

    if (onView) {
      menuOptions.push({
        id: 'view',
        title: 'View',
        icon: <VisibilityOutlined />,
        onClick: () => onView(trainingMaterial),
      })
    }

    if (onDelete) {
      menuOptions.push({
        id: 'delete',
        title: 'Delete',
        icon: <DeleteOutlineOutlined color='error' />,
        onClick: onOpenDeleteConfirmationDialog,
      })
    }

    return menuOptions
  }, [onDelete, onEdit, onOpenDeleteConfirmationDialog, onView, trainingMaterial])

  return (
    <>
      <Tooltip text='Open menu'>
        <IconButton ref={menuAnchorRef} edge='end' size='small' onClick={onOpenMenu}>
          <MoreVertOutlined />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={menuAnchorRef.current}
        open={isMenuOpen}
        onClose={onCloseMenu}
        onClick={onCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {options.map(({ id, title, icon, onClick }) => (
          <MenuItem key={id} title={title} onClick={onClick}>
            <ListItemIcon>{icon}</ListItemIcon>
            {title}
          </MenuItem>
        ))}
      </Menu>

      <TrainingMaterialDeleteConfirmationDialog
        open={isDeleteConfirmationDialogOpen}
        onClose={onCloseDeleteConfirmationDialog}
        onSubmit={onDeleteSubmit}
      />
    </>
  )
}

export default memo(TrainingMaterialMenu)

